import React, { useEffect, useState } from 'react';
import { Alert, Button, Col, Form, Modal, Row } from '@/components/ui';
import { areasService } from '../../../services/areasService';
import type { Vendor } from '../../../services/vendorService';
import { vendorService } from '../../../services/vendorService';
import { missingCallerServiceAreaIds } from '../utils/callerAreas';

interface VendorFormData {
  name: string;
  legalName?: string;
  contactEmail?: string;
  phoneNumber?: string;
  comments?: string;
  serviceAreaIds: number[];
}

interface Props {
  show: boolean;
  onHide: () => void;
  vendor?: Vendor | null;
  onSave: (data: VendorFormData) => Promise<void>;
  callerAreas?: Array<{ id: number; name: string }>;
  canAddArea?: boolean;
  onAdoptVendor?: (vendor: Vendor) => void;
}

const VendorModal: React.FC<Props> = ({
  show,
  onHide,
  vendor,
  onSave,
  callerAreas = [],
  canAddArea = false,
  onAdoptVendor,
}) => {
  const [form, setForm] = useState<VendorFormData>({ name: '', serviceAreaIds: [] });
  const [serviceAreas, setServiceAreas] = useState<Array<{ id: number; name: string }>>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [matches, setMatches] = useState<Vendor[]>([]);

  useEffect(() => {
    areasService
      .getServiceAreas()
      .then((areas: any[]) => setServiceAreas(areas ?? []))
      .catch(() => setServiceAreas([]));
  }, []);

  useEffect(() => {
    if (!show) return;
    setError(null);
    setMatches([]);
    setForm(
      vendor
        ? {
            name: vendor.name,
            legalName: vendor.legalName ?? '',
            contactEmail: vendor.contactEmail ?? '',
            phoneNumber: vendor.phoneNumber ?? '',
            comments: vendor.comments ?? '',
            serviceAreaIds: vendor.serviceAreas?.map((sa) => sa.serviceAreaId) ?? [],
          }
        : { name: '', legalName: '', contactEmail: '', phoneNumber: '', comments: '', serviceAreaIds: [] }
    );
  }, [show, vendor]);

  useEffect(() => {
    if (!show || vendor) {
      setMatches([]);
      return;
    }
    const q = form.name.trim();
    if (q.length < 2) {
      setMatches([]);
      return;
    }
    let cancelled = false;
    const handle = window.setTimeout(() => {
      vendorService
        .getVendorsPaginated({ search: q, limit: 5 })
        .then((res) => {
          if (!cancelled) setMatches(res.data ?? []);
        })
        .catch(() => {
          if (!cancelled) setMatches([]);
        });
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [show, vendor, form.name]);

  const toggleArea = (id: number) => {
    setForm((prev) => ({
      ...prev,
      serviceAreaIds: prev.serviceAreaIds.includes(id)
        ? prev.serviceAreaIds.filter((x) => x !== id)
        : [...prev.serviceAreaIds, id],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError('A vendor name is required.');
      return;
    }
    if (form.serviceAreaIds.length === 0) {
      // Mirrors the server rule rather than letting the request 409: a vendor with no
      // service area would be invisible to everyone, including whoever just created it.
      setError('Select at least one service area — a vendor with none would be visible to nobody.');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await onSave({ ...form, name: form.name.trim() });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save vendor');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="2xl">
      <Modal.Header closeButton>
        <Modal.Title>{vendor ? `Edit ${vendor.name}` : 'Add Vendor'}</Modal.Title>
      </Modal.Header>
      {/* The form sits between the panel and Modal.Body, so it must carry the flex-column
          contract itself. Without it Body grows to full content height and the footer is
          clipped by the panel's overflow. */}
      <Form onSubmit={handleSubmit} className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          {!vendor && (
            <Alert variant="info">
              New vendors are created <strong>pending approval</strong>. Search first — if this
              subcontractor already exists in another service area, add it to yours instead of
              creating a duplicate. Resources can be added immediately; someone with Activate Vendor
              permission must still activate the vendor before it can be used, and activation is
              blocked until valid WCB and liability insurance documents are attached.
            </Alert>
          )}

          {!vendor && matches.length > 0 && (
            <Alert variant="warning">
              <div className="tw-font-medium tw-mb-2">Existing vendors with a similar name</div>
              <ul className="tw-mb-0 tw-pl-4">
                {matches.map((m) => {
                  const areas = m.serviceAreas?.map((sa) => sa.serviceArea.name).join(', ') || '—';
                  const canAdopt =
                    canAddArea && missingCallerServiceAreaIds(m, callerAreas.map((a) => a.id)).length > 0;
                  return (
                    <li key={m.id} className="tw-mb-1">
                      <span className="tw-font-medium">{m.name}</span>
                      <span className="tw-text-content-muted"> · {areas}</span>
                      {canAdopt && (
                        <Button
                          variant="link"
                          size="sm"
                          className="tw-p-0 tw-ml-2 tw-align-baseline"
                          type="button"
                          onClick={() => onAdoptVendor?.(m)}
                        >
                          Add to my area
                        </Button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </Alert>
          )}

          <Row>
            <Col md={6}>
              <Form.Group className="tw-mb-3">
                <Form.Label>Vendor Name *</Form.Label>
                <Form.Control
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="tw-mb-3">
                <Form.Label>Legal Name</Form.Label>
                <Form.Control
                  type="text"
                  value={form.legalName ?? ''}
                  onChange={(e) => setForm({ ...form, legalName: e.target.value })}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="tw-mb-3">
                <Form.Label>Contact Email</Form.Label>
                <Form.Control
                  type="email"
                  value={form.contactEmail ?? ''}
                  onChange={(e) => setForm({ ...form, contactEmail: e.target.value })}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="tw-mb-3">
                <Form.Label>Phone Number</Form.Label>
                <Form.Control
                  type="tel"
                  value={form.phoneNumber ?? ''}
                  onChange={(e) => setForm({ ...form, phoneNumber: e.target.value })}
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="tw-mb-3">
            <Form.Label>Service Areas *</Form.Label>
            <div className="tw-text-sm tw-text-content-muted tw-mb-2">
              The areas this vendor is approved to operate in. A vendor can operate in several —
              resources and rates are then set per area.
            </div>
            <div className="tw-flex tw-flex-wrap tw-gap-3">
              {serviceAreas.map((sa) => (
                <Form.Check
                  key={sa.id}
                  type="checkbox"
                  id={`vendor-sa-${sa.id}`}
                  label={sa.name}
                  checked={form.serviceAreaIds.includes(sa.id)}
                  onChange={() => toggleArea(sa.id)}
                />
              ))}
            </div>
          </Form.Group>

          <Form.Group className="tw-mb-3">
            <Form.Label>Comments</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={form.comments ?? ''}
              onChange={(e) => setForm({ ...form, comments: e.target.value })}
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline-secondary" type="button" onClick={onHide} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={saving}>
            {saving ? 'Saving…' : vendor ? 'Save Changes' : 'Create Vendor'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default VendorModal;
